import { createUseStyles } from "react-jss";
import { ITheme } from "../../constants/theme";
import {
  BreakPointDesktop,
  BreakPointMobile,
  BreakPointTablet,
} from "../../constants/dimension";
import { getMediaSizeScale } from "../../models/screen";

const scaledPx = (value: number) => `${value * getMediaSizeScale()}px`;

export const useStyles = createUseStyles((theme: ITheme) => ({
  root: {
    boxSizing: "border-box",
    position: "relative",
    zIndex: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    overflow: "scroll",
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    background: "#000",
    paddingTop: 56,
    gap: theme.spacing.space16,
    [BreakPointMobile]: {
      overflow: "scroll",
    },
    [BreakPointTablet]: {
      overflow: "hidden",
      paddingTop: 96,
      gap: 92,
      alignItems: "center",
    },
    [BreakPointDesktop]: {
      gap: theme.spacing.space48,
      alignItems: "center",
    },
  },

  background: {
    position: "fixed",
    zIndex: -1,
    width: "100%",
    height: "100%",
    top: 0,
    left: 0,
    pointerEvents: "none",
    background: "#000000",
    [BreakPointDesktop]: {
      background:
        "linear-gradient(257deg, #1F0E2A 2.25%, #000 38.97%, #0E192A 97.5%), radial-gradient(30.14% 32.48% at 39.9% 50.01%, #3D70B7 0%, #000 100%)",
    },
    [BreakPointTablet]: {
      background:
        "linear-gradient(257deg, #1F0E2A 2.25%, #000 38.97%, #0E192A 97.5%)",
    },
  },

  header: {
    position: "fixed",
    zIndex: theme.zIndex.normal + 1,
    top: theme.spacing.space12,
    left: theme.spacing.space8,
    cursor: "pointer",
    display: "flex",
    [BreakPointDesktop]: {
      borderRadius: 20,
      background: "rgba(255, 255, 255, 0.10)",
      backdropFilter: "blur(24.545454025268555px)",
      width: 96,
      height: 96,
      top: 40,
      left: 90,
      alignItems: "center",
      justifyContent: "center",
    },
  },

  iconHeader: {
    padding: theme.spacing.space8,
  },

  headerText: {
    textAlign: "center",
    padding: `0 ${theme.spacing.space16}px`,
  },

  mainText: {
    textTransform: "uppercase",
    fontSize: "24px !important",
    lineHeight: "33.6px !important",
    color: "#0AF6FB !important",
    [BreakPointTablet]: {
      fontSize: "45px !important",
      lineHeight: "63px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "60px !important",
      lineHeight: "84px !important",
    },
  },

  subText: {
    marginTop: theme.spacing.space8,
    fontSize: "16px !important",
    lineHeight: "22.4px !important",
    [BreakPointTablet]: {
      fontSize: "32px !important",
      lineHeight: "44.8px !important",
      marginTop: 0,
    },
    [BreakPointDesktop]: {
      fontSize: "36px !important",
      lineHeight: "50.4px !important",
      marginTop: 0,
    },
  },

  bottomBar: {
    boxSizing: "border-box",
    position: "fixed",
    width: "100%",
    zIndex: theme.zIndex.appBar,
    left: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(0deg, #191919 0%, #191919 100%), rgba(255, 255, 255, 0.10)",
    borderTop: "1px solid rgba(255, 255, 255, 0.10)",
    padding: `${theme.spacing.space8}px ${theme.spacing.space16}px`,
    [BreakPointTablet]: {
      padding: `${theme.spacing.space16}px ${theme.spacing.space24}px`,
    },
    [BreakPointDesktop]: {
      padding: `${theme.spacing.space24}px 90px`,
    },
  },

  contentSection: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    padding: `0 ${theme.spacing.space16}px`,
    paddingBottom: 120,
    [BreakPointTablet]: {
      padding: `0 ${theme.spacing.space24}px`,
      paddingBottom: 120,
    },
    [BreakPointDesktop]: {
      width: "auto",
      padding: 0,
      paddingBottom: 120,
      marginTop: "auto",
      marginBottom: "auto",
    },
    [BreakPointMobile]: {
      "@media (orientation: landscape) and (max-width: 931px)": {
        width: "auto",
        justifyContent: "center",
      },
    },
  },

  /* --- Smart Console Bundle card ------------------------------------- */
  bundleConsoleSection: {
    boxSizing: "border-box",
    display: "grid",
    width: 400,
    maxWidth: "100%",
    margin: "0 auto",
    gridTemplateColumns:
      "[info-start] 33% [art-start] 12% [img-start] 31.5% [info-end] 23.5% [img-end art-end]",
    gridTemplateRows:
      "[info-start] 24px [art-start img-start] auto [info-end] 32px [art-end img-end]",
    [BreakPointTablet]: {
      width: 740,
      maxWidth: "calc(100vw - 48px)",
      gridTemplateColumns:
        "[info-start] 33% [img-start] 16% [art-start] 11% [info-end] 25% [img-end] 15% [art-end]",
      gridTemplateRows:
        "[art-start] minmax(52px, 1fr) [info-start img-start] auto [info-end img-end] minmax(52px, 1fr) [art-end]",
    },
    [BreakPointDesktop]: {
      width: 1166,
      maxWidth: "none",
      gridTemplateColumns:
        "4% [info-start] 29% [img-start] 16% [art-start] 11% [info-end] 25% [img-end] 15% [art-end]",
      gridTemplateRows: `[art-start] minmax(${scaledPx(
        72,
      )}, 1fr) [info-start img-start] auto [info-end img-end] minmax(${scaledPx(
        72,
      )}, 1fr) [art-end]`,
    },
  },

  backgroundCardWrapper: {
    gridColumn: "art",
    gridRow: "art",
    zIndex: 1,
    overflow: "hidden",
    borderRadius: theme.spacing.space16,
    [BreakPointTablet]: {
      borderRadius: theme.spacing.space24,
    },
    [BreakPointDesktop]: {
      borderRadius: theme.spacing.space40,
    },
  },

  backgroundCard: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "right bottom",
  },

  bundleImgZoom: {
    [BreakPointDesktop]: {
      transform: "scale(1.1)",
    },
  },

  productImg: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "contain",
    position: "absolute",
    bottom: 0,
    left: "50%",
    [BreakPointMobile]: {
      width: "65%",
      height: "auto",
      maxHeight: "70%",
      bottom: 0,
      right: "-24%",
      left: "auto",
      objectPosition: "right bottom",
      zIndex: 3,
    },
  },

  inforCard: {
    boxSizing: "border-box",
    gridColumn: "info",
    gridRow: "info",
    alignSelf: "start",
    zIndex: 2,
    minWidth: 0,
    background: "rgba(94, 116, 144, 0.40)",
    backdropFilter: "blur(20.876476287841797px)",
    borderRadius: theme.spacing.space16,
    padding: `${theme.spacing.space16}px ${theme.spacing.space12}px`,
    [BreakPointTablet]: {
      zIndex: 2,
      borderRadius: theme.spacing.space24,
      padding: theme.spacing.space32,
    },
    [BreakPointDesktop]: {
      zIndex: 2,
      borderRadius: theme.spacing.space40,
      padding: `${theme.spacing.space48}px 60px ${theme.spacing.space48}px ${theme.spacing.space48}px`,
    },
    position: "relative",
  },

  featuresList: {
    display: "flex",
    flexDirection: "column",
  },

  featureItem: {
    display: "flex",
    alignItems: "center",
    "& + &": {
      marginTop: theme.spacing.space2,
      [BreakPointTablet]: {
        marginTop: theme.spacing.space4,
      },
      [BreakPointDesktop]: {
        marginTop: theme.spacing.space8,
      },
    },
  },

  featureIcon: {
    flexShrink: 0,
    marginRight: theme.spacing.space4,
    fontSize: "12px !important",
    lineHeight: "12px !important",
    [BreakPointTablet]: {
      fontSize: "16px !important",
      lineHeight: "16px !important",
    },
    [BreakPointDesktop]: {
      marginRight: theme.spacing.space8,
      fontSize: "24px !important",
      lineHeight: "24px !important",
    },
  },

  featureText: {
    fontSize: "12px !important",
    lineHeight: "16.8px !important",
    [BreakPointTablet]: {
      fontSize: "14px !important",
      lineHeight: "19.6px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "18px !important",
      lineHeight: "25.2px !important",
    },
  },

  bundleTitle: {
    fontSize: "18px !important",
    lineHeight: "25.2px !important",
    marginBottom: theme.spacing.space8,
    "@media (max-width: 359px)": {
      fontSize: "16px !important",
      lineHeight: "22.4px !important",
    },
    [BreakPointTablet]: {
      fontSize: "28px !important",
      lineHeight: "39.2px !important",
      marginBottom: theme.spacing.space12,
    },
    [BreakPointDesktop]: {
      fontSize: "40px !important",
      lineHeight: "56px !important",
      marginBottom: theme.spacing.space16,
    },
  },

  perMonth: {
    [BreakPointMobile]: {
      fontSize: `0.5rem !important`,
    },
    [BreakPointTablet]: {
      fontSize: "10px !important",
      lineHeight: "14px !important",
    },
  },

  startingAt: {
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing.space16,
    [BreakPointTablet]: {
      marginTop: theme.spacing.space24,
    },
    [BreakPointDesktop]: {
      marginTop: theme.spacing.space48,
    },
  },

  startingAtLabel: {
    fontSize: "10px !important",
    lineHeight: "14px !important",
    [BreakPointTablet]: {
      fontSize: "14px !important",
      lineHeight: "19.6px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "20px !important",
      lineHeight: "28px !important",
    },
  },

  founderPrice: {
    paddingTop: 100,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.space4,
    [BreakPointMobile]: {
      paddingTop: theme.spacing.space32,
      flexDirection: "column",
      alignItems: "start",
    },
    [BreakPointTablet]: {
      paddingTop: 60,
    },
  },

  founderPriceWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.space4,
    [BreakPointMobile]: {
      gap: theme.spacing.space2,
    },
  },

  strikeThrough: {
    position: "relative",
    display: "inline-block",

    "&::after": {
      content: '""',
      position: "absolute",
      left: "-5%",
      top: "45%",
      width: "110%",
      height: "1px",
      background: theme.colors.TextSilver,
      transform: "rotate(15deg)",
      transformOrigin: "center",
    },
  },

  oldPrice: {
    color: `${theme.colors.TextSilver} !important`,
    [BreakPointMobile]: {
      fontSize: "0.5rem !important",
      lineHeight: "11.2px !important",
    },
    [BreakPointTablet]: {
      fontSize: "8px !important",
      lineHeight: "11.2px !important",
    },
  },

  founderPriceText: {
    color: "#0AF6FB !important",
    fontSize: "16px !important",
    lineHeight: "22.4px !important",
    [BreakPointTablet]: {
      fontSize: "20px !important",
      lineHeight: "28px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "32px !important",
      lineHeight: "44.8px !important",
    },
  },

  bundlePrice: {
    fontSize: "16px !important",
    lineHeight: "22.4px !important",
    [BreakPointTablet]: {
      fontSize: "20px !important",
      lineHeight: "28px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "32px !important",
      lineHeight: "44.8px !important",
    },
  },

  /* --- Renewal (monthly / yearly toggle) ----------------------------- */

  renewalSection: {
    alignSelf: "stretch",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  planToggle: {
    boxSizing: "border-box",
    display: "flex",
    width: 280,
    maxWidth: "100%",
    padding: theme.spacing.space8,
    marginTop: theme.spacing.space8,
    marginBottom: theme.spacing.space24,
    borderRadius: 100,
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(2px)",
    [BreakPointTablet]: {
      width: 390,
      marginBottom: theme.spacing.space48,
    },
    [BreakPointDesktop]: {
      width: 390,
      marginBottom: 36,
    },
  },

  planToggleOption: {
    position: "relative",
    flex: "1 1 0",
    minWidth: 0,
    padding: `${theme.spacing.space12}px ${theme.spacing.space8}px`,
    border: "none",
    borderRadius: 100,
    background: "transparent",
    color: theme.colors.TextDefault,
    cursor: "pointer",
    '&[aria-checked="true"]': {
      background: theme.colors.Primary,
    },
    [BreakPointTablet]: {
      padding: `${theme.spacing.space16}px ${theme.spacing.space8}px`,
    },
    [BreakPointDesktop]: {
      padding: `${theme.spacing.space16}px ${theme.spacing.space8}px`,
    },
  },

  planToggleFocused: {
    outline: `2px solid ${theme.colors.Border4}`,
    outlineOffset: 2,
  },

  planToggleLabel: {
    display: "block",
    whiteSpace: "nowrap",
    fontSize: "12px !important",
    lineHeight: "16.8px !important",
    [BreakPointTablet]: {
      fontSize: "16px !important",
      lineHeight: "22.4px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "16px !important",
      lineHeight: "22.4px !important",
    },
  },

  planToggleTag: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translate(-50%, -60%)",
    padding: `0 ${theme.spacing.space8}px`,
    borderRadius: 100,
    background: "linear-gradient(76deg, #FD487E 33.98%, #C30543 79.82%)",
    whiteSpace: "nowrap",
    fontSize: "10px !important",
    lineHeight: "16px !important",
    [BreakPointDesktop]: {
      fontSize: "12px !important",
      lineHeight: "20px !important",
    },
  },

  renewalPrice: {
    display: "flex",
    alignItems: "baseline",
    marginTop: theme.spacing.space32,
    [BreakPointDesktop]: {
      marginTop: 100,
    },
  },

  renewalPriceUnit: {
    marginLeft: theme.spacing.space4,
    fontSize: "10px !important",
    lineHeight: "14px !important",
    [BreakPointTablet]: {
      fontSize: "12px !important",
      lineHeight: "16.8px !important",
    },
    [BreakPointDesktop]: {
      fontSize: "16px !important",
      lineHeight: "22.4px !important",
    },
  },

  /* --- Cloud Console Access card -------------------------------------- */

  buyBtn: {
    fontSize: "12px !important",
    fontWeight: `${theme.fontWeight.medium} !important`,
    borderRadius: `${theme.radius.button}px`,
    marginTop: theme.spacing.space4,
    [BreakPointTablet]: {
      fontSize: "10px !important",
      fontWeight: `${theme.fontWeight.medium} !important`,
      marginTop: theme.spacing.space4,
      borderRadius: `${theme.radius.sSmall}px`,
    },
    [BreakPointDesktop]: {
      fontSize: `${theme.spacing.space16}px !important`,
      fontWeight: `${theme.fontWeight.semiBold} !important`,
      marginTop: theme.spacing.space8,
      padding: `${theme.spacing.space12}px ${theme.spacing.space48}px !important`,
      borderRadius: `${theme.radius.small}px`,
    },
  },

  buyBtnEs: {
    [BreakPointDesktop]: {
      padding: `${theme.spacing.space12}px ${theme.spacing.space20}px !important`,
    },
    [BreakPointMobile]: {
      maxWidth: "55%",
      whiteSpace: "normal",
      textAlign: "center",
      padding: `${theme.spacing.space4}px ${theme.spacing.space6}px !important`,
    },
  },
}));
